<script setup>
import AutocompleteFilter from '@/components/widgets/AutocompleteFilter.vue';
import { inject, onMounted, ref, watch } from 'vue';
import { useInfinite } from '@/composables/use-infinity';

const backendApi = inject('backendApi');

const props = defineProps({
    hideLabel: {
        type: Boolean,
        default: false
    },
    args: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['update', 'update:focused']);

const selected = ref(null);
const previousAgencyId = ref(null);

const fetchAgents = ({ pattern, page, limit }) =>
    backendApi().agents().index({
        pattern,
        agency: props.args?.agency?.id,
        active: true,
        page,
        limit
    });

const {
    items: agents,
    loading: isLoading,
    showRetryButton,
    hasMore,
    searchQuery,
    getItems,
    loadMore,
    reset
} = useInfinite(fetchAgents);

const handleSearch = () => {
    reset();
    getItems();
};

// Watch for changes to the agency ID specifically
watch(
    () => props.args?.agency?.id,
    (newAgencyId) => {
        // Only refresh if the agency ID has actually changed
        if (newAgencyId !== previousAgencyId.value) {
            previousAgencyId.value = newAgencyId;
            selected.value = null;
            emit('update', null); // Clear the selected value in parent
            handleSearch(); // Force a fresh fetch when agency changes
        }
    },
    {
        immediate: true
    }
);

// Initial data fetch on mount
onMounted(() => {
    getItems();
});
</script>

<template>
    <AutocompleteFilter
        v-model="selected"
        v-model:search="searchQuery"
        :items="agents"
        item-title="name"
        item-value="id"
        :label="hideLabel ? '' : $t('Agent')"
        :loading="isLoading"
        :show-retry-button="showRetryButton"
        :infinite-scroll="hasMore"
        @infinite-scroll="loadMore"
        @update="emit('update', selected)"
        @preparing-search="handleSearch"
        @search="handleSearch"
        @retry="handleSearch"
        @update:focused="(state) => emit('update:focused', state)"
    >
        <template v-slot:default="{ props, item }">
            <v-list-item
                v-bind="props"
                :title="`${item.raw.name} ~ ${item.raw.address}`"
                :subtitle="`${item.raw.email}`"
            ></v-list-item>
        </template>
        <template v-if="$slots.prepend" v-slot:prepend>
            <slot name="prepend"></slot>
        </template>
    </AutocompleteFilter>
</template>
